import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Textarea } from "../components/ui/textarea";
import { Slider } from "../components/ui/slider";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  Brain,
  Zap,
  Battery,
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  CheckCircle,
  Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOOD_OPTIONS = [
  { value: "tired", emoji: "😫", label: "Tired", color: "bg-gray-500" },
  { value: "stressed", emoji: "😰", label: "Stressed", color: "bg-orange-500" },
  { value: "unmotivated", emoji: "😔", label: "Unmotivated", color: "bg-blue-500" },
  { value: "neutral", emoji: "😐", label: "Neutral", color: "bg-gray-400" },
  { value: "motivated", emoji: "🙂", label: "Motivated", color: "bg-green-400" },
  { value: "energized", emoji: "😊", label: "Energized", color: "bg-green-500" },
  { value: "unstoppable", emoji: "🔥", label: "Unstoppable", color: "bg-primary" }
];

export default function MoodCheckin() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [todayCheckin, setTodayCheckin] = useState(null);
  const [selectedMood, setSelectedMood] = useState("");
  const [energyLevel, setEnergyLevel] = useState(5);
  const [notes, setNotes] = useState("");
  const [history, setHistory] = useState([]);
  const [insights, setInsights] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [todayRes, historyRes, insightsRes] = await Promise.all([
        axios.get(`${API}/mood/today`),
        axios.get(`${API}/mood/history?days=14`),
        axios.get(`${API}/adaptation/insights`)
      ]);
      
      if (todayRes.data.checkin) {
        setTodayCheckin(todayRes.data.checkin);
        setSelectedMood(todayRes.data.checkin.mood);
        setEnergyLevel(todayRes.data.checkin.energy_level);
        setNotes(todayRes.data.checkin.notes || "");
      }
      
      setHistory(historyRes.data.history || []);
      setInsights(insightsRes.data.insights || []);
    } catch (error) {
      console.error("Failed to fetch mood data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedMood) {
      toast.error("Please select how you're feeling");
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/mood/checkin`, {
        mood: selectedMood,
        energy_level: energyLevel,
        notes: notes || null
      });
      
      toast.success("Check-in logged! Coach Coop will adapt your experience.");
      fetchData();
    } catch (error) {
      toast.error("Failed to save check-in");
    } finally {
      setSaving(false);
    }
  };

  const getMoodTrend = () => {
    if (history.length < 3) return null;
    
    const recentAvg = history.slice(0, 3).reduce((sum, h) => sum + h.energy_level, 0) / 3;
    const olderAvg = history.slice(3, 6).reduce((sum, h) => sum + h.energy_level, 0) / Math.min(history.slice(3, 6).length, 3);
    
    if (recentAvg > olderAvg + 0.5) return "up";
    if (recentAvg < olderAvg - 0.5) return "down";
    return "stable";
  };

  const trend = getMoodTrend();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary animate-pulse text-2xl font-bold">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" /> Back
            </Link>
            <h1 className="font-bold tracking-tight uppercase">Daily Check-In</h1>
            <div className="w-16" />
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 max-w-4xl mx-auto">
        {/* Already Checked In Banner */}
        {todayCheckin && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-accent/10 border border-accent/30 rounded-sm flex items-center gap-3"
          >
            <CheckCircle className="h-5 w-5 text-accent" />
            <div>
              <div className="font-bold text-sm">Today's Check-In Complete</div>
              <div className="text-xs text-muted-foreground">You can update it anytime</div>
            </div>
          </motion.div>
        )}

        {/* Mood Selection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                <Brain className="h-5 w-5 text-primary" />
                How Are You Feeling Today?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                {MOOD_OPTIONS.map((mood) => {
                  const isSelected = selectedMood === mood.value;
                  
                  return (
                    <motion.div
                      key={mood.value}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Card
                        data-testid={`mood-${mood.value}`}
                        onClick={() => setSelectedMood(mood.value)}
                        className={`cursor-pointer transition-all ${
                          isSelected
                            ? "border-primary bg-primary/10 ring-2 ring-primary"
                            : "border-border hover:border-primary/50"
                        }`}
                      >
                        <CardContent className="p-3 text-center">
                          <div className="text-3xl mb-1">{mood.emoji}</div>
                          <div className="text-xs font-bold uppercase tracking-tight">{mood.label}</div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Energy Level */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                <Battery className="h-5 w-5 text-yellow-500" />
                Energy Level
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 mb-4">
                <span className="text-4xl font-mono font-black text-primary">{energyLevel}</span>
                <span className="text-muted-foreground">/ 10</span>
              </div>
              <Slider
                data-testid="energy-slider"
                value={[energyLevel]}
                onValueChange={([val]) => setEnergyLevel(val)}
                min={1}
                max={10}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-2">
                <span>Running on empty</span>
                <span>Full power</span>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Notes */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="uppercase tracking-tighter text-sm">
                Anything else? (Optional)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Slept badly, big day at work, feeling sore..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="bg-input border-transparent focus:border-primary rounded-sm"
              />
            </CardContent>
          </Card>
        </motion.div>

        {/* Submit Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8"
        >
          <Button
            data-testid="mood-submit-btn"
            onClick={handleSubmit}
            disabled={saving || !selectedMood}
            className="w-full bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest rounded-sm py-6"
          >
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : todayCheckin ? "Update Check-In" : "Log Check-In"}
          </Button>
        </motion.div>

        {/* Insights */}
        {insights.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-8"
          >
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                  <Zap className="h-5 w-5 text-yellow-500" />
                  Insights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {insights.map((insight, i) => (
                    <div
                      key={i}
                      className={`p-3 rounded-sm ${
                        insight.type === "positive" ? "bg-accent/10 border border-accent/30" :
                        insight.type === "warning" ? "bg-orange-500/10 border border-orange-500/30" :
                        "bg-secondary/50 border border-border"
                      }`}
                    >
                      <p className="text-sm">{insight.text}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Mood Trend */}
        {history.length >= 3 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                  {trend === "up" && <TrendingUp className="h-5 w-5 text-accent" />}
                  {trend === "down" && <TrendingDown className="h-5 w-5 text-orange-500" />}
                  {trend === "stable" && <Minus className="h-5 w-5 text-muted-foreground" />}
                  Your Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 mb-4">
                  <div className={`text-lg font-bold ${
                    trend === "up" ? "text-accent" :
                    trend === "down" ? "text-orange-500" :
                    "text-muted-foreground"
                  }`}>
                    {trend === "up" && "Energy is improving! 📈"}
                    {trend === "down" && "Consider lighter workouts 💆"}
                    {trend === "stable" && "Holding steady 📊"}
                  </div>
                </div>
                
                {/* Mini History */}
                <div className="flex gap-1">
                  {history.slice(0, 14).reverse().map((h, i) => (
                    <div
                      key={i}
                      className="flex-1 flex flex-col items-center gap-1"
                    >
                      <div
                        className="w-full bg-secondary rounded-sm"
                        style={{ height: `${h.energy_level * 6}px` }}
                      >
                        <div
                          className={`w-full h-full rounded-sm ${
                            h.energy_level >= 7 ? "bg-accent" :
                            h.energy_level >= 4 ? "bg-primary" :
                            "bg-orange-500"
                          }`}
                        />
                      </div>
                      <span className="text-[8px] text-muted-foreground">
                        {new Date(h.date).getDate()}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </main>
    </div>
  );
}
